/**
 * Shared game utilities - pure functions for state manipulation
 * Used by both GameEngine and AIPlayer to avoid duplication
 */

import type { Point, Segment, GameState, Move, OccupiedPoint } from './types';

// ============== Geometry Functions ==============

/** Calculate Euclidean distance between two points */
export function distance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Check if two points are equal */
export function pointsEqual(p1: Point, p2: Point): boolean {
  return p1.x === p2.x && p1.y === p2.y;
}

/** Check if a point lies on a segment (excluding endpoints) */
export function pointOnSegment(point: Point, segment: Segment): boolean {
  const { from: a, to: b } = segment;

  // Check if point is collinear with segment
  const cross = (point.y - a.y) * (b.x - a.x) - (point.x - a.x) * (b.y - a.y);
  if (Math.abs(cross) > 0.0001) return false;

  // Check if point is between a and b (exclusive of endpoints)
  const dotProduct = (point.x - a.x) * (b.x - a.x) + (point.y - a.y) * (b.y - a.y);
  const squaredLength = (b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y);

  return dotProduct > 0 && dotProduct < squaredLength;
}

/** Check if two segments intersect (excluding shared endpoints) */
export function segmentsIntersect(seg1: Segment, seg2: Segment): boolean {
  const { from: a, to: b } = seg1;
  const { from: c, to: d } = seg2;

  // Skip if segments share an endpoint
  if (pointsEqual(a, c) || pointsEqual(a, d) || pointsEqual(b, c) || pointsEqual(b, d)) {
    return false;
  }

  const direction = (p1: Point, p2: Point, p3: Point): number =>
    (p3.x - p1.x) * (p2.y - p1.y) - (p2.x - p1.x) * (p3.y - p1.y);

  const onSegment = (p: Point, q: Point, r: Point): boolean =>
    q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);

  const d1 = direction(c, d, a);
  const d2 = direction(c, d, b);
  const d3 = direction(a, b, c);
  const d4 = direction(a, b, d);

  if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
      ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) {
    return true;
  }

  if (d1 === 0 && onSegment(c, a, d)) return true;
  if (d2 === 0 && onSegment(c, b, d)) return true;
  if (d3 === 0 && onSegment(a, c, b)) return true;
  if (d4 === 0 && onSegment(a, d, b)) return true;

  return false;
}

// ============== State Utilities ==============

/** Deep clone a game state */
export function cloneState(state: GameState): GameState {
  return {
    currentPlayer: state.currentPlayer,
    players: {
      1: {
        pos: state.players[1].pos ? { ...state.players[1].pos } : null,
        path: state.players[1].path.map(p => ({ ...p })),
        lastDist: state.players[1].lastDist,
      },
      2: {
        pos: state.players[2].pos ? { ...state.players[2].pos } : null,
        path: state.players[2].path.map(p => ({ ...p })),
        lastDist: state.players[2].lastDist,
      },
    },
    allPoints: state.allPoints.map(p => ({ ...p })),
    allSegments: state.allSegments.map(s => ({ from: { ...s.from }, to: { ...s.to } })),
    gameOver: state.gameOver,
    winner: state.winner,
    gridSize: state.gridSize,
  };
}

/** Check if a point is occupied in a state */
export function isPointOccupiedInState(state: GameState, point: Point): boolean {
  return state.allPoints.some(p => pointsEqual(p, point));
}

/** Check if a segment is valid in a state (doesn't pass through points or cross segments) */
export function isValidSegmentInState(
  state: GameState,
  segment: Segment,
  excludePoint: Point | null = null
): boolean {
  // Check if segment passes through any point
  for (const point of state.allPoints) {
    if (excludePoint && pointsEqual(point, excludePoint)) continue;
    if (pointOnSegment(point, segment)) return false;
  }

  // Check for intersections with existing segments
  for (const existingSeg of state.allSegments) {
    if (segmentsIntersect(segment, existingSeg)) return false;
  }

  return true;
}

/** Apply a move to a state and return a new state (pure function) */
export function applyMoveToState(state: GameState, move: Move, playerNum: 1 | 2): GameState {
  const newState = cloneState(state);
  const player = newState.players[playerNum];
  const moveDist = distance(move.from, move.to);

  const fromPoint: OccupiedPoint = { x: move.from.x, y: move.from.y, player: playerNum };
  const toPoint: OccupiedPoint = { x: move.to.x, y: move.to.y, player: playerNum };
  const segment: Segment = { from: { ...move.from }, to: { ...move.to } };

  if (move.isFirstMove) {
    newState.allPoints.push(fromPoint, toPoint);
    newState.allSegments.push(segment);
    player.path.push({ ...move.from }, { ...move.to });
  } else {
    newState.allPoints.push(toPoint);
    newState.allSegments.push(segment);
    player.path.push({ ...move.to });
  }

  player.pos = { ...move.to };
  player.lastDist = moveDist;
  newState.currentPlayer = playerNum === 1 ? 2 : 1;

  return newState;
}

/** Get all valid moves for a player in a given state */
export function getValidMovesForState(state: GameState, playerNum: 1 | 2): Move[] {
  const player = state.players[playerNum];
  const moves: Move[] = [];
  const { gridSize } = state;

  if (!player.pos) {
    // First move - find all valid starting lines (avoid duplicates by checking half)
    for (let y1 = 0; y1 < gridSize; y1++) {
      for (let x1 = 0; x1 < gridSize; x1++) {
        const from: Point = { x: x1, y: y1 };
        if (isPointOccupiedInState(state, from)) continue;

        for (let y2 = y1; y2 < gridSize; y2++) {
          const startX = y2 === y1 ? x1 + 1 : 0;
          for (let x2 = startX; x2 < gridSize; x2++) {
            const to: Point = { x: x2, y: y2 };
            if (isPointOccupiedInState(state, to)) continue;

            const segment: Segment = { from, to };
            if (isValidSegmentInState(state, segment)) {
              moves.push({
                from: { ...from },
                to: { ...to },
                isFirstMove: true,
                dist: distance(from, to),
              });
            }
          }
        }
      }
    }
  } else {
    // Subsequent moves - must start from current position
    for (let gy = 0; gy < gridSize; gy++) {
      for (let gx = 0; gx < gridSize; gx++) {
        const to: Point = { x: gx, y: gy };
        if (pointsEqual(to, player.pos)) continue;
        if (isPointOccupiedInState(state, to)) continue;

        const moveDist = distance(player.pos, to);
        if (moveDist <= player.lastDist) continue;

        const segment: Segment = { from: player.pos, to };
        if (isValidSegmentInState(state, segment, player.pos)) {
          moves.push({
            from: { ...player.pos },
            to: { ...to },
            dist: moveDist,
            isFirstMove: false,
          });
        }
      }
    }
  }

  return moves;
}

/** Check if a player can make any valid move */
export function canPlayerMoveInState(state: GameState, playerNum: 1 | 2): boolean {
  const player = state.players[playerNum];
  const { gridSize } = state;

  if (!player.pos) {
    // First move - check if any valid line exists (early return)
    for (let y1 = 0; y1 < gridSize; y1++) {
      for (let x1 = 0; x1 < gridSize; x1++) {
        const from: Point = { x: x1, y: y1 };
        if (isPointOccupiedInState(state, from)) continue;

        for (let y2 = 0; y2 < gridSize; y2++) {
          for (let x2 = 0; x2 < gridSize; x2++) {
            if (x1 === x2 && y1 === y2) continue;
            const to: Point = { x: x2, y: y2 };
            if (isPointOccupiedInState(state, to)) continue;

            if (isValidSegmentInState(state, { from, to })) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  // Subsequent move - check if any valid move from current position
  for (let gy = 0; gy < gridSize; gy++) {
    for (let gx = 0; gx < gridSize; gx++) {
      const to: Point = { x: gx, y: gy };
      if (pointsEqual(to, player.pos)) continue;
      if (isPointOccupiedInState(state, to)) continue;

      const moveDist = distance(player.pos, to);
      if (moveDist <= player.lastDist) continue;

      if (isValidSegmentInState(state, { from: player.pos, to }, player.pos)) {
        return true;
      }
    }
  }

  return false;
}
